import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { FaPlay, FaDownload, FaEye, FaCamera, FaVideo, FaEdit, FaLightbulb, FaTrash } from "react-icons/fa";
import { FaImage } from "react-icons/fa6";
import "./Gallery.css";
import { FaLock } from "react-icons/fa";
import GalleryLightbox from "./GalleryLightbox";
import {
  getAllGalleryImages,
  getGalleryZipLink,
  downloadGalleryZipFromCdn,
} from "../../../../../utils/APIs/galleryApis";
import { getOneUpcomingBookingById } from "../../../../../utils/APIs/bookingApis";
import { getAccessToken } from "../../../../../utils/APIs/commonHeadApiLogic";
import { deleteMedia } from "../../../../../utils/APIs/editingApis";

import Loader from "../../../../../Template/Loader/Loader";
import CommonMessageModal from "../../../../CommonMessageModal/CommonMessageModal";
import UnlockImageModal from "./UnlockImageModal/UnlockImageModal";
import { toast } from "react-toastify";

// const LIMIT = 33;
const LIMIT = 33;
// const LIMIT = 2;

const normalizeHourTab = (hours) => {
  const h = String(hours ?? "").replace(/[^0-9-]/g, "");
  if (!h) return "";
  if (h === "0-3" || h === "3") return "0-3";
  if (h === "5") return "5";
  if (h === "8") return "8";
  return h;
};

const isVideoKey = (key) =>
  /\.(mp4|webm|ogg|mov|mkv)$/i.test(key || "");

const loadedImagesCache = new Set();

class MediaLoadQueue {
  constructor(concurrency = 4) {
    this.concurrency = concurrency;
    this.running = 0;
    this.queue = [];
  }

  register(onStart) {
    const item = { onStart };
    this.queue.push(item);
    this.next();
    return () => {
      this.queue = this.queue.filter((q) => q !== item);
    };
  }

  next() {
    if (this.running >= this.concurrency || this.queue.length === 0) return;

    this.running++;
    const item = this.queue.shift();

    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      this.running--;
      this.next();
    };

    item.onStart(finish);
    setTimeout(finish, 2500); // 2.5s safety timeout
  }
}

const mediaLoadQueue = new MediaLoadQueue(4);

const VirtualGalleryItem = ({ children, src }) => {
  const [hasBeenVisible, setHasBeenVisible] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (hasBeenVisible) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setHasBeenVisible(true);
        }
      },
      {
        rootMargin: "1200px 0px 1200px 0px",
        threshold: 0.01,
      }
    );

    const currentRef = containerRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [hasBeenVisible]);

  const placeholderStyle = { 
    aspectRatio: "16/10", 
    width: "100%", 
    height: "100%",
    background: "#eee", 
    borderRadius: "4px" 
  };

  const isAlreadyCached = src && loadedImagesCache.has(src);
  const placeholderClass = `gallery-placeholder-box ${isAlreadyCached ? "" : "loading-skeleton"}`;

  return (
    <div ref={containerRef} className="virtual-gallery-item-wrapper" style={{ width: "100%", height: "100%", position: "absolute", top: 0, left: 0 }}>
      {hasBeenVisible ? (
        children
      ) : (
        <div style={placeholderStyle} className={placeholderClass} />
      )}
    </div>
  );
};

const LazyVideo = React.forwardRef(({ src, isBlur, onClick, onLoadedMetadata, onError, style }, ref) => {
  const localRef = useRef(null);

  const setRefs = useCallback((node) => {
    localRef.current = node;
    if (typeof ref === 'function') ref(node);
    else if (ref) ref.current = node;
  }, [ref]);


  useEffect(() => {
    const videoEl = localRef.current;
    if (!videoEl) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          const playPromise = videoEl.play();
          if (playPromise !== undefined) {
            playPromise.catch(() => {});
          }
        } else {
          videoEl.pause();
        }
      },
      {
        rootMargin: "100px",
        threshold: 0.01
      }
    );

    observer.observe(videoEl);
    return () => {
      observer.unobserve(videoEl);
    };
  }, []);

  return (
    <video
      ref={setRefs}
      src={src}
      muted
      playsInline
      preload="metadata"
      crossOrigin="anonymous"
      className="gallery-video"
      style={{
        filter: isBlur ? "blur(12px)" : "none",
        pointerEvents: isBlur ? "none" : "auto",
        width: "100%",
        height: "100%",
        objectFit: "cover",
        cursor: "pointer",
        ...style
      }}
      onLoadedMetadata={onLoadedMetadata}
      onError={onError}
      onClick={onClick}
      onTimeUpdate={(e) => {
        if (e.target.currentTime >= 5) {
          e.target.currentTime = 0;
          const playPromise = e.target.play();
          if (playPromise !== undefined) {
            playPromise.catch(() => {});
          }
        }
      }}
    />
  );
});

const LazyMedia = ({ src, fileKey, isBlur, isVideo, onClick, index }) => {
  const [shouldLoad, setShouldLoad] = useState(() => loadedImagesCache.has(src));
  const [isLoaded, setIsLoaded] = useState(() => loadedImagesCache.has(src));
  const mediaRef = useRef(null);
  const finishLoadRef = useRef(null);

  const handleLoad = useCallback(() => {
    setIsLoaded(true);
    loadedImagesCache.add(src);
    if (finishLoadRef.current) {
      finishLoadRef.current();
      finishLoadRef.current = null;
    }
  }, [src]);

  const handleError = useCallback(() => {
    if (finishLoadRef.current) {
      finishLoadRef.current();
      finishLoadRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (shouldLoad) return;
    const unregister = mediaLoadQueue.register((finish) => {
      finishLoadRef.current = finish;
      setShouldLoad(true);
    });
    return () => {
      unregister();
      if (finishLoadRef.current) {
        finishLoadRef.current();
        finishLoadRef.current = null;
      }
    };
  }, [src, shouldLoad]);

  useEffect(() => {
    if (!shouldLoad) return;
    const el = mediaRef.current;
    if (!el) return;
    if (isVideo) {
      if (el.readyState >= 1) handleLoad();
    } else {
      if (el.complete) handleLoad();
    }
  }, [isVideo, handleLoad, shouldLoad]);

  return (
    <VirtualGalleryItem src={src}>
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden"
        }}
        className={!isLoaded ? "loading-skeleton" : ""}
      >
        {shouldLoad ? (
          !isVideo ? (
            <img
              ref={mediaRef}
              src={src}
              alt={`gallery-${index}`}
              onClick={onClick}
              decoding="async"
              crossOrigin="anonymous"
              onLoad={handleLoad}
              onError={handleError}
              style={{
                filter: isBlur ? "blur(12px)" : "none",
                pointerEvents: isBlur ? "none" : "auto",
                width: "100%",
                height: "100%",
                objectFit: "cover",
                cursor: "pointer",
                visibility: isLoaded ? "visible" : "hidden"
              }}
            />
          ) : (
            <LazyVideo
              ref={mediaRef}
              src={src}
              isBlur={isBlur}
              onClick={onClick}
              onLoadedMetadata={handleLoad}
              onError={handleError}
              style={{ visibility: isLoaded ? "visible" : "hidden" }}
            />
          )
        ) : null}
      </div>
    </VirtualGalleryItem>
  );
};

const GalleryItem = React.memo(({
  src,
  fileKey,
  isBlur,
  showDelete,
  index,
  isSelected,
  openLightbox,
  deleteUserFile,
  downloadFile,
  toggleSelect
}) => {
  const isVideo = isVideoKey(fileKey);

  return (
    <div
      className={`gallery-box video-box ${isSelected ? "selected" : ""}`}
      style={{
        position: "relative",
        cursor: isBlur ? "default" : "pointer"
      }}
      onClick={() => !isBlur && openLightbox(index)}
    >
      <LazyMedia
        src={src}
        fileKey={fileKey}
        isBlur={isBlur}
        isVideo={isVideo}
        onClick={() => !isBlur && openLightbox(index)}
        index={index}
      />

      {/* LOCK OVERLAY */}
      {isBlur && (
        <div className="blur-lock">
          <FaLock className="lock-icon m-2" />
          <span>Locked</span>
        </div>
      )}

      {/* Existing overlay & actions */}
      {!isBlur && (
        <>
          {isVideo && (
            <div className="play-overlay" onClick={() => openLightbox(index)}>
              <FaPlay className="play-icon" />
            </div>
          )}

          <div className="gallery-hover">
            <div className="gallery-actions-hover">
              {showDelete && (
                <button
                  className="gallery-hover-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteUserFile(fileKey);
                  }}
                >
                  <FaTrash />
                </button>
              )}
              <button
                className="gallery-hover-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  downloadFile(fileKey);
                }}
              >
                <FaDownload />
              </button>

              <button
                className="gallery-hover-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  openLightbox(index);
                }}
              >
                <FaEye />
              </button>
            </div>

            <div className="gallery-checkbox">
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => toggleSelect(src)}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
});

const getPackageCategory = (pkg) =>
  (
    pkg?.subCategoryType ||
    pkg?.planCategory ||
    pkg?.planId?.planCategory ||
    pkg?.planId?.teamCategory ||
    pkg?.category ||
    "standard"
  )
    .toString()
    .toLowerCase();

const getBookedHourTabs = (booking, planTab = "") => {
  const tabs = [];
  const add = (hours) => {
    const key = normalizeHourTab(hours);
    if (key && !tabs.includes(key)) tabs.push(key);
  };

  const pkgs = booking?.hourlyPackages || [];
  const scopedPkgs = planTab
    ? pkgs.filter((pkg) =>
        getPackageCategory(pkg).includes(String(planTab).toLowerCase())
      )
    : pkgs;

  scopedPkgs.forEach((pkg) => {
    add(pkg.hours);
    add(pkg.durationValue);
    add(pkg.planId?.pricingOptions?.[0]?.durationValue);
  });

  if (tabs.length === 0) {
    add(booking?.hours);
    add(booking?.plan_id?.hours);
  }

  const order = ["0-3", "5", "8"];
  return tabs.sort((a, b) => {
    const ai = order.indexOf(a);
    const bi = order.indexOf(b);
    if (ai === -1 && bi === -1) return a.localeCompare(b);
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });
};

const formatHourTabLabel = (hour) =>
  hour === "0-3" ? "0-3 Hrs" : `${hour} Hrs`;

const Gallery = ({ bookingId, photographerId, clientId, serviceName }) => {
  console.log("Client ID:", clientId);

  const [selectedItems, setSelectedItems] = useState([]);
  const [galleryData, setGalleryData] = useState([]);
  const [isBlur, setIsBlur] = useState(false)
  // full_Payment
  const [isFullPayment, setFullPayment] = useState(true)
  const [daysRemaining, setDaysRemaining] = useState(false)
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [isPublished, setIsPublished] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  // New sub-navigation states
  const [activeHourTab, setActiveHourTab] = useState("0-3");
  const [activeServiceCard, setActiveServiceCard] = useState("photography");
  const [galleryEditingTab, setGalleryEditingTab] = useState("received"); // 'sent' or 'received'
  const [galleryPlanTab, setGalleryPlanTab] = useState(serviceName?.toLowerCase().includes('premium') ? 'premium' : 'standard');
  const [detailsLoading, setDetailsLoading] = useState(true);

  // Hourly Folder step and staff navigation states
  const [currentStep, setCurrentStep] = useState("SERVICES"); // 'SERVICES' | 'STAFF' | 'MEDIA'
  const [activeStaff, setActiveStaff] = useState(null); // { id, name }

  const cleanId = (val) => {
    if (!val) return "";
    if (typeof val === 'string') return val.trim().toLowerCase();
    if (typeof val === 'object') {
      if (val._id) return String(val._id).trim().toLowerCase();
      if (val.id) return String(val.id).trim().toLowerCase();
      if (typeof val.toString === 'function') return String(val.toString()).trim().toLowerCase();
    }
    return String(val).trim().toLowerCase();
  };

  const getActiveHourlyPlan = () => {
    const pkgs = bookingDetails?.hourlyPackages || [];
    return (
      pkgs.find((pkg) => {
        const pkgCategory =
          pkg.subCategoryType ||
          pkg.planCategory ||
          pkg.planId?.planCategory ||
          pkg.planId?.teamCategory ||
          pkg.category ||
          "standard";
        const matchesCategory = pkgCategory
          .toLowerCase()
          .includes(galleryPlanTab?.toLowerCase());

        const pkgHours = String(pkg.hours || "").replace(/[^0-9-]/g, "");
        const activeH = String(activeHourTab || "").replace(/[^0-9-]/g, "");
        const matchesHour =
          pkgHours === activeH ||
          (activeH === "0-3" && (pkgHours === "0-3" || pkgHours === "3"));

        return matchesCategory && matchesHour;
      }) || null
    );
  };

  const getStaffList = () => {
    const pkg = getActiveHourlyPlan();
    if (!pkg) return [];

    let ids = [];
    let names = [];

    if (activeServiceCard === "photography") {
      ids = pkg.assignedPhotographers || [];
      names = pkg.assignedPhotographerNames || [];
    } else if (activeServiceCard === "videography") {
      ids = pkg.assignedVideographers || [];
      names = pkg.assignedVideographerNames || [];
    } else if (activeServiceCard === "editor") {
      ids = pkg.assignedEditors || [];
      names = pkg.assignedEditorNames || [];
    } else if (activeServiceCard === "lighting") {
      ids = pkg.assignedLightingSetups || pkg.assignedLighting || [];
      names = pkg.assignedLightingNames || [];
    }

    const staffList = [];
    ids.forEach((idVal, index) => {
      const id = cleanId(idVal);
      if (!id) return;

      let name = names[index];
      if (!name && typeof idVal === "object" && idVal !== null) {
        name =
          idVal.name || idVal.username || idVal.basicInfo?.fullName;
      }
      if (!name) name = "Unknown";

      if (!staffList.some((s) => cleanId(s.id) === id)) {
        staffList.push({ id, name });
      }
    });

    return staffList;
  };

  const [showMessageModal, setShowMessageModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [bookingDetails, setBookingDetails] = useState(null);
  const loaderRef = useRef(null);
  const isFetchingRef = useRef(false);

  const isEditing = bookingId === 'dummy-editing' || 
    bookingDetails?.serviceCategory === 'editing' || 
    bookingDetails?.service_id?.serviceName?.toLowerCase().includes('editing') ||
    serviceName?.toLowerCase().includes('editing');

  const isQuoteEditingBooking = (bookingDetails?.serviceCategory === 'editing' || bookingDetails?.serviceCategory === 'Editing') && 
        (!bookingDetails?.editingbookings || bookingDetails?.editingbookings.length === 0 || bookingDetails?.isQuote || !!bookingDetails?.quoteId);

  const isActualHourly = bookingId === 'dummy-hourly' || 
    bookingDetails?.serviceCategory === 'hourly' || 
    bookingDetails?.service_id?.serviceName?.toLowerCase().includes('hourly') ||
    serviceName?.toLowerCase().includes('hourly');

  const isHourly = isActualHourly && !bookingDetails?.quoteId;
  const hourlyBookedHourTabs = useMemo(
    () =>
      isHourly ? getBookedHourTabs(bookingDetails, galleryPlanTab) : [],
    [isHourly, bookingDetails, galleryPlanTab]
  );
  const hourlyPlanTabs = (() => {
    if (!isHourly) return [];

    const tabs = [];
    const addTab = (value) => {
      if (!value) return;
      const normalized = String(value).toLowerCase();
      if (normalized.includes("premium")) {
        if (!tabs.includes("premium")) tabs.push("premium");
      } else if (normalized.includes("standard")) {
        if (!tabs.includes("standard")) tabs.push("standard");
      }
    };

    addTab(serviceName);
    addTab(bookingDetails?.service_id?.serviceName);
    addTab(bookingDetails?.subCategoryType);
    addTab(bookingDetails?.subCategory);

    (bookingDetails?.hourlyPackages || []).forEach((pkg) => {
      addTab(pkg?.subCategoryType);
      addTab(pkg?.planCategory);
      addTab(pkg?.category);
      addTab(pkg?.planId?.planCategory);
      addTab(pkg?.planId?.teamCategory);
    });

    // Fallback: if we still can't infer type, keep both tabs visible.
    if (tabs.length === 0) return ["standard", "premium"];
    return tabs;
  })();

  useEffect(() => {
    if (!isHourly) return;
    if (hourlyPlanTabs.length === 0) return;
    if (!hourlyPlanTabs.includes(galleryPlanTab)) {
      setGalleryPlanTab(hourlyPlanTabs[0]);
    }
  }, [isHourly, hourlyPlanTabs, galleryPlanTab]);

  useEffect(() => {
    if (!isHourly || hourlyBookedHourTabs.length === 0) return;
    if (!hourlyBookedHourTabs.includes(activeHourTab)) {
      setActiveHourTab(hourlyBookedHourTabs[0]);
    }
  }, [isHourly, hourlyBookedHourTabs, activeHourTab]);

  const fetchGallery = useCallback(async (currentPage) => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    if (currentPage > 1) {
    }

    // Intercept dummy bookings
    if (bookingId?.startsWith("dummy-")) {
      const mockImages = [
        { id: '1', url: 'https://images.unsplash.com/photo-1511285560929-80b456fea0bc?auto=format&fit=crop&w=400&q=60', key: 'img1.jpg', type: 'photography' },
        { id: '2', url: 'https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=400&q=60', key: 'img2.jpg', type: 'photography' },
        { id: '3', url: 'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?auto=format&fit=crop&w=400&q=60', key: 'img3.jpg', type: 'photography' },
        { id: '4', url: 'https://images.unsplash.com/photo-1519225495810-751783d9cfd7?auto=format&fit=crop&w=400&q=60', key: 'img4.jpg', type: 'photography' },
        { id: '5', url: 'https://images.unsplash.com/photo-1470770841072-f978cf4d019e?auto=format&fit=crop&w=400&q=60', key: 'img5.jpg', type: 'photography' },
        { id: '6', url: 'https://images.unsplash.com/photo-1501785888041-af3ef285b470?auto=format&fit=crop&w=400&q=60', key: 'video1.mp4', type: 'videography' },
        { id: '7', url: 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?auto=format&fit=crop&w=400&q=60', key: 'video2.mp4', type: 'videography' },
        { id: '8', url: 'https://images.unsplash.com/photo-1472214103451-9374bd1c798e?auto=format&fit=crop&w=400&q=60', key: 'img6.jpg', type: 'photography' },
      ];
      setGalleryData(mockImages);
      setHasMore(false);
      setLoading(false);
      isFetchingRef.current = false;
      return;
    }

    try {
      let queryCategory = "";
      let querySource = "";

      if (isHourly) {
        // Do not filter by plan category at API level — uploads are often tagged
        // Standard while the user browses Premium (same issue as admin gallery).
        queryCategory = "";
        querySource = activeStaff?.id ? "photographer" : "";
      } else if (isEditing) {
        if (galleryEditingTab === "sent") {
          queryCategory = galleryPlanTab
            ? galleryPlanTab.charAt(0).toUpperCase() + galleryPlanTab.slice(1)
            : "";
          querySource = "user";
        } else {
          // Received: load all published editor deliverables, filter by plan tab in UI
          queryCategory = "";
          querySource = "photographer";
        }
      } else if (galleryPlanTab) {
        queryCategory = galleryPlanTab.charAt(0).toUpperCase() + galleryPlanTab.slice(1);
        querySource = "";
      }

      const targetPhotographerId = isHourly
        ? (activeStaff?.id ? cleanId(activeStaff.id) : cleanId(photographerId?._id || photographerId))
        : isEditing
          ? ""
          : cleanId(photographerId?._id || photographerId);

      const response = await getAllGalleryImages(
        currentPage,
        LIMIT,
        bookingId,
        queryCategory,
        querySource,
        targetPhotographerId
      );

      if (response?.data?.success) {
        const files = response?.data?.data || [];

        const images = files.map((item, index) => ({
          id: `${currentPage}-${index}`,
          key: item.key,
          url: item.imageUrl,
          imageUrl: item.imageUrl,
          clientId: item.clientId,
          photographerId: item.photographerId,
          category: item.category
        }));

        console.log("RESPONSE GALLERy", response?.data)
        setIsBlur(response?.data?.isblur)
        setFullPayment(response?.data?.full_payment)
        setDaysRemaining(response?.data?.remainingDays)
        setIsPublished(Boolean(response?.data?.isPublished));

        setGalleryData((prev) =>
          currentPage === 1 ? images : [...prev, ...images]
        );

        setHasMore(Boolean(response?.data?.pagination?.hasMore));
      }
    } catch (error) {
      console.error("Gallery API Error:", error);
      setHasMore(false);
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId, photographerId, isBlur, daysRemaining, galleryPlanTab, galleryEditingTab, isHourly, isEditing, activeStaff]);

  /* ===============================
      DOWNLOAD full ZIP
  =============================== */

  const buildZipFilterParams = () => {
    let category = "";
    let source = "";
    let targetPhotographerId = photographerId?._id || photographerId || "";

    if (isHourly) {
      category = "";
      source = activeStaff?.id ? "photographer" : "";
      targetPhotographerId = activeStaff?.id ? activeStaff.id : targetPhotographerId;
    } else if (isEditing) {
      category = galleryPlanTab
        ? galleryPlanTab.charAt(0).toUpperCase() + galleryPlanTab.slice(1)
        : "";
      source = galleryEditingTab === "sent" ? "user" : "photographer";
      targetPhotographerId = "";
    } else if (galleryPlanTab) {
      category = galleryPlanTab.charAt(0).toUpperCase() + galleryPlanTab.slice(1);
    }

    return {
      bookingId,
      category,
      source,
      photographerId: targetPhotographerId,
    };
  };

  const handleDownloadZip = async () => {
    try {
      const zipFilters = buildZipFilterParams();
      
      await downloadGalleryZipFromCdn(zipFilters, {
        clientId: clientId?._id || clientId || "",
        onWaiting: () => toast.info("Preparing download from cloud storage..."),
      });
      toast.success("Zip Download Started...");
    } catch (error) {
      setModalMessage(error.message || "Download failed");
      setShowMessageModal(true);
    }
  };

  /* ===============================
      INITIAL LOAD
  =============================== */

  useEffect(() => {
    if (isHourly) {
      if (currentStep !== "MEDIA" || !activeStaff?.id) {
        setGalleryData([]);
        setSelectedItems([]);
        setHasMore(false);
        setPage(1);
        isFetchingRef.current = false;
        return;
      }
    } else {
      if (!photographerId && !isEditing && !bookingId?.startsWith("dummy-")) {
        setGalleryData([]);
        setSelectedItems([]);
        setHasMore(false);
        setPage(1);
        isFetchingRef.current = false;
        return;
      }
    }

    setGalleryData([]);
    setSelectedItems([]);
    setHasMore(true);
    isFetchingRef.current = false;
    setPage(1);

    fetchGallery(1);
  }, [bookingId, photographerId, isEditing, fetchGallery, galleryPlanTab, galleryEditingTab, isHourly, currentStep, activeStaff]);

  useEffect(() => {
    const fetchBookingDetails = async () => {
      if (!bookingId) return;

      try {
        const response = await getOneUpcomingBookingById(bookingId);
        const data = response.data.data;
        setBookingDetails(data);

          // Initialize tabs based on real booking data
          const sName = data?.service_id?.serviceName || "";
          const isHourlyBooking = (data?.serviceCategory === "hourly" || sName.toLowerCase().includes("hourly")) && !data?.quoteId;
          const isEditingBooking = data?.serviceCategory === "editing" || sName.toLowerCase().includes("editing");

          if (isHourlyBooking) {
            const planTabs = [];
            const addPlanTab = (value) => {
              if (!value) return;
              const normalized = String(value).toLowerCase();
              if (normalized.includes("premium")) {
                if (!planTabs.includes("premium")) planTabs.push("premium");
              } else if (normalized.includes("standard")) {
                if (!planTabs.includes("standard")) planTabs.push("standard");
              }
            };

            addPlanTab(sName);
            addPlanTab(data?.subCategoryType);
            (data?.hourlyPackages || []).forEach((pkg) => {
              addPlanTab(pkg?.subCategoryType);
              addPlanTab(pkg?.planCategory);
              addPlanTab(pkg?.category);
              addPlanTab(pkg?.planId?.planCategory);
              addPlanTab(pkg?.planId?.teamCategory);
            });

            const selectedPlanTab = planTabs[0] || "standard";
            setGalleryPlanTab(selectedPlanTab);

            const bookedHoursForPlan = getBookedHourTabs(data, selectedPlanTab);
            const initialHourTab = bookedHoursForPlan[0] || "0-3";
            setActiveHourTab(initialHourTab);

            // Auto-select first booked service and its assigned staff member on mount
            const pkgs = data?.hourlyPackages || [];
            const planTab = selectedPlanTab;

            let autoService = "photography";
            let autoStaff = null;

            const servicesToCheck = [
              { id: "photography", keyId: "assignedPhotographers", keyNames: "assignedPhotographerNames" },
              { id: "videography", keyId: "assignedVideographers", keyNames: "assignedVideographerNames" },
              { id: "editor", keyId: "assignedEditors", keyNames: "assignedEditorNames" },
              { id: "lighting", keyId: "assignedLightingSetups", keyNames: "assignedLightingNames" }
            ];

            for (const s of servicesToCheck) {
              const matchedPkg = pkgs.find((pkg) => {
                const pkgCat =
                  pkg.subCategoryType ||
                  pkg.planCategory ||
                  pkg.planId?.planCategory ||
                  pkg.planId?.teamCategory ||
                  pkg.category ||
                  "standard";
                const matchesCategory = pkgCat
                  .toLowerCase()
                  .includes(planTab);
                const matchesHour =
                  normalizeHourTab(pkg.hours) ===
                  normalizeHourTab(initialHourTab);
                return matchesCategory && matchesHour;
              });

              if (matchedPkg) {
                const ids = matchedPkg[s.keyId] || [];
                const names = matchedPkg[s.keyNames] || [];
                if (ids.length > 0) {
                  const id = cleanId(ids[0]);
                  let name = names[0];
                  if (
                    !name &&
                    typeof ids[0] === "object" &&
                    ids[0] !== null
                  ) {
                    name =
                      ids[0].name ||
                      ids[0].username ||
                      ids[0].basicInfo?.fullName;
                  }
                  if (!name) name = "Unknown";
                  autoService = s.id;
                  autoStaff = { id, name };
                  break;
                }
              }
            }

            if (autoStaff) {
              setActiveServiceCard(autoService);
              setActiveStaff(autoStaff);
              // Do not auto-skip to MEDIA, user wants to see the SERVICES categories first.
            }
          } else if (isEditingBooking) {
            const stdCount = data.standardCount !== undefined ? data.standardCount : (data.standardEditingVideos || 0);
            const premCount = data.premiumCount !== undefined ? data.premiumCount : (data.premiumEditingVideos || 0);
            if (premCount > 0 && stdCount === 0) {
              setGalleryPlanTab("premium");
            } else if (stdCount > 0) {
              setGalleryPlanTab("standard");
            } else {
              // Fallback to plan name
              const plan = data?.editingbookings?.[0];
              const pName = plan?.planName || sName;
              if (pName) {
                setGalleryPlanTab(pName.toLowerCase().includes("premium") ? "premium" : "standard");
              }
            }
          }
      } catch (error) {
        console.error("Error fetching booking details for gallery:", error);
      } finally {
        setDetailsLoading(false);
      }
    };

    fetchBookingDetails();
  }, [bookingId]);

  useEffect(() => {
    if (page === 1) return;
    if (isHourly) {
      if (currentStep !== "MEDIA" || !activeStaff?.id) return;
    } else {
      if (!photographerId && !isEditing && !bookingId?.startsWith("dummy-")) return;
    }

    fetchGallery(page);
  }, [page, photographerId, bookingId, isEditing, fetchGallery, isHourly, currentStep, activeStaff]);

  /* ===============================
      INFINITE SCROLL
  =============================== */

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isFetchingRef.current) {
          setPage((prev) => prev + 1);
        }
      },
      { threshold: 0, rootMargin: "800px" }
    );

    if (loaderRef.current) observer.observe(loaderRef.current);

    return () => observer.disconnect();
  }, [hasMore]);

  useEffect(() => {
    const handleScrollLoad = () => {
      if (!hasMore || isFetchingRef.current) return;
      const nearBottom =
        window.innerHeight + window.scrollY >=
        document.documentElement.scrollHeight - 600;
      if (nearBottom) {
        setPage((prev) => prev + 1);
      }
    };

    window.addEventListener("scroll", handleScrollLoad, { passive: true });
    return () => window.removeEventListener("scroll", handleScrollLoad);
  }, [hasMore]);

  /* ===============================
      IMAGE ACTIONS
  =============================== */

  const toggleSelect = useCallback((url) => {
    setSelectedItems((prev) =>
      prev.includes(url)
        ? prev.filter((item) => item !== url)
        : [...prev, url]
    );
  }, []);

  const openPreview = (url) => window.open(url, "_blank");
console.log(openPreview)

  const isSentEditingMedia = (media) => {
    const imgCat = media.category?.toLowerCase() || '';
    const tabCat = galleryPlanTab?.toLowerCase() || 'standard';
    return !media.photographerId && (imgCat === tabCat || (!imgCat && tabCat === 'standard') || imgCat === 'editing' || imgCat === 'custom');
  };

  const isReceivedGalleryMedia = (media) => {
    const isNormalBooking = !isHourly && !isEditing;
    if (isNormalBooking) return media.photographerId;

    if (isEditing && galleryEditingTab === "received") {
      return !!media.photographerId;
    }

    if (isHourly) {
      const matchesStaff = activeStaff && cleanId(media.photographerId) === cleanId(activeStaff.id);
      return media.photographerId && matchesStaff;
    }

    const imgCat = media.category?.toLowerCase() || '';
    const tabCat = galleryPlanTab?.toLowerCase() || 'standard';
    return media.photographerId && (imgCat === tabCat || (!imgCat && tabCat === 'standard') || imgCat === 'editing' || imgCat === 'custom');
  };

  const visibleGalleryData = galleryData.filter((media) =>
    isEditing && galleryEditingTab === 'sent'
      ? isSentEditingMedia(media)
      : isReceivedGalleryMedia(media)
  );

  const visibleSelectedCount = visibleGalleryData.filter((item) =>
    selectedItems.includes(item.url)
  ).length;

  useEffect(() => {
    if (!bookingId || bookingId.startsWith("dummy-")) return;
    if (isHourly && !activeStaff?.id) return;
    if (!isPublished || visibleGalleryData.length === 0) return;

    const zipFilters = buildZipFilterParams();
    getGalleryZipLink(zipFilters).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    bookingId,
    isHourly,
    isEditing,
    galleryPlanTab,
    galleryEditingTab,
    activeStaff,
    photographerId,
    isPublished,
    visibleGalleryData.length,
  ]);

  // Grid Dimensions Calculations
  const gridRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(1200);

  useEffect(() => {
    const updateDimensions = () => {
      if (gridRef.current) {
        setContainerWidth(gridRef.current.getBoundingClientRect().width || 1200);
      }
    };

    window.addEventListener("resize", updateDimensions);
    
    // Initial measurement
    updateDimensions();
    
    // Set a timeout to let dyn layouts settle
    const t = setTimeout(updateDimensions, 150);

    return () => {
      window.removeEventListener("resize", updateDimensions);
      clearTimeout(t);
    };
  }, []);

  const { startIndex, totalRows, rowHeight, visibleItems, cols, colWidth } = useMemo(() => {
    const totalItems = visibleGalleryData.length;
    const gap = 4;
    const colMinWidth = 260;
    const cols = Math.max(1, Math.floor((containerWidth + gap) / (colMinWidth + gap)));
    const colWidth = (containerWidth - (cols - 1) * gap) / cols;
    const rowHeight = colWidth + gap;
    const totalRows = Math.ceil(totalItems / cols);

    return {
      startIndex: 0,
      totalRows,
      rowHeight,
      visibleItems: visibleGalleryData,
      cols,
      colWidth
    };
  }, [visibleGalleryData, containerWidth]);

  // Lightbox States
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(-1);

  const openLightbox = useCallback((index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  }, []);

  const closeLightbox = () => {
    setLightboxOpen(false);
    setLightboxIndex(-1);
  };

  const handleLightboxNext = useCallback(() => {
    if (lightboxIndex < visibleGalleryData.length - 1) {
      setLightboxIndex((prev) => prev + 1);
    }
  }, [lightboxIndex, visibleGalleryData.length]);

  const handleLightboxPrev = useCallback(() => {
    if (lightboxIndex > 0) {
      setLightboxIndex((prev) => prev - 1);
    }
  }, [lightboxIndex]);

  // Touch Swipe for Lightbox (Mobile support)
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  const handleTouchStart = (e) => {
    touchStartX.current = e.changedTouches[0].screenX;
  };

  const handleTouchEnd = (e) => {
    touchEndX.current = e.changedTouches[0].screenX;
    const diff = touchStartX.current - touchEndX.current;
    if (diff > 50) {
      handleLightboxNext();
    } else if (diff < -50) {
      handleLightboxPrev();
    }
  };

  // Keyboard navigation for Lightbox
  useEffect(() => {
    if (!lightboxOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === "ArrowRight") {
        handleLightboxNext();
      } else if (e.key === "ArrowLeft") {
        handleLightboxPrev();
      } else if (e.key === "Escape") {
        closeLightbox();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [lightboxOpen, handleLightboxNext, handleLightboxPrev]);

  // Disable body scroll when Lightbox is open
  useEffect(() => {
    if (lightboxOpen) {
      const originalBodyOverflow = window.getComputedStyle(document.body).overflow;
      const originalHtmlOverflow = window.getComputedStyle(document.documentElement).overflow;
      
      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";
      
      return () => {
        document.body.style.overflow = originalBodyOverflow;
        document.documentElement.style.overflow = originalHtmlOverflow;
      };
    }
  }, [lightboxOpen]);

  // Auto load next page of images if we approach the end inside Lightbox
  useEffect(() => {
    if (!lightboxOpen || lightboxIndex < 0) return;

    if (
      lightboxIndex >= visibleGalleryData.length - 3 &&
      hasMore &&
      !loading &&
      !isFetchingRef.current
    ) {
      setPage((prev) => prev + 1);
    }
  }, [lightboxIndex, lightboxOpen, visibleGalleryData.length, hasMore, loading]);

  const deleteUserFile = useCallback(async (key) => {
    if (!window.confirm("Are you sure you want to delete this file?")) return;
    try {
      setLoading(true);
      const res = await deleteMedia(bookingId, key);
      if (res?.data?.success || res?.status === 200) {
        setGalleryData(prev => prev.filter(item => item.key !== key));
        setModalMessage("File deleted successfully.");
        setShowMessageModal(true);
      } else {
        throw new Error(res?.data?.message || "Delete failed");
      }
    } catch (error) {
      console.error("Delete Error:", error);
      setModalMessage(error.message || "Delete failed");
      setShowMessageModal(true);
    } finally {
      setLoading(false);
    }
  }, [bookingId]);

  // single img downloade 
  const downloadFile = useCallback(async (key) => {
    try {
      const token = getAccessToken();
      const baseUrl = (process.env.REACT_APP_PHOTOGRAPHER_USER_WEBSITE_BASE_API_URL || process.env.REACT_APP_API_URL || "").trim();

      const params = new URLSearchParams({
        key: key,
        bookingId: bookingId,
        token: token
      });

      const downloadUrl = `${baseUrl}/api/users/downloadSingleFile?${params.toString()}`;
      window.location.href = downloadUrl;

    } catch (error) {
      console.error("Single Download Error:", error);
      setModalMessage(error.message || "Failed to download file");
      setShowMessageModal(true);
    }
  }, [bookingId]);

  const deleteSelectedFiles = async () => {
    if (!window.confirm("Are you sure you want to delete the selected files?")) return;
    try {
      const selectedKeys = visibleGalleryData
        .filter((item) => selectedItems.includes(item.url))
        .map((item) => item.key);

      if (selectedKeys.length === 0) {
        setModalMessage("Please select images to delete.");
        setShowMessageModal(true);
        return;
      }

      setLoading(true);
      for (const key of selectedKeys) {
        await deleteMedia(bookingId, key);
      }
      
      setGalleryData((prev) =>
        prev.filter((item) => !selectedKeys.includes(item.key))
      );
      setSelectedItems([]);
      setModalMessage("Selected files deleted successfully.");
      setShowMessageModal(true);
    } catch (error) {
      console.error("Delete Selected Error:", error);
      setModalMessage(error.message || "Delete failed");
      setShowMessageModal(true);
    } finally {
      setLoading(false);
    }
  };

  // slected file downloade
  const downloadSelected = async () => {
    const selectedKeys = visibleGalleryData
      .filter((item) => selectedItems.includes(item.url))
      .map((item) => item.key);

    if (selectedKeys.length === 0) {
      setModalMessage("Please select images to download.");
      setShowMessageModal(true);
      return;
    }

    try {
      const zipFilters = buildZipFilterParams();
      await downloadGalleryZipFromCdn(
        {
          ...zipFilters,
          keys: selectedKeys,
        },
        {
          clientId: clientId?._id || clientId || "",
          onWaiting: () => toast.info("Preparing download from cloud storage..."),
        }
      );
      toast.success("Zip Download Started...");
    } catch (error) {
      setModalMessage(error.message || "Download failed");
      setShowMessageModal(true);
    }
  };

  // GalleryItem has been moved outside the component as React.memo

  const renderStaffFolders = () => {
    const staffList = getStaffList();

    return (
      <div className="staff-folders-view">
        <div className="row g-3" style={{ display: "flex", flexWrap: "wrap", gap: "15px" }}>
          {staffList.length === 0 ? (
            <div className="col-12 text-center py-5 bg-light rounded border w-100">
              <p className="text-muted mb-0">No staff assigned to this role yet.</p>
            </div>
          ) : (
            staffList.map(staff => {
              const initials = staff.name
                .split(' ')
                .slice(0, 2)
                .map(w => w[0]?.toUpperCase() || '')
                .join('');
              return (
                <div key={staff.id} style={{ flex: "1 0 200px", maxWidth: "250px" }}>
                  <div className="pro-person-card" onClick={() => handlePhotographerClick(staff)}>
                    <div className="pro-person-avatar">{initials}</div>
                    <div className="pro-person-info">
                      <div className="pro-person-name">{staff.name}</div>
                    </div>
                    <div className="pro-person-action">
                      <FaImage className="pro-person-gallery-icon" style={{ marginRight: "4px" }} />
                      <span>View Gallery</span>
                      <span className="pro-person-arrow" style={{ marginLeft: "4px" }}> &rarr; </span>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    );
  };

  const handlePhotographerClick = (staff) => {
    setActiveStaff({ id: staff.id, name: staff.name });
    setCurrentStep("MEDIA");
  };

  /* ===============================
      COMING SOON UI
  =============================== */

  // Helper to render the coming soon state
  const renderComingSoon = () => (
    <div className="gallery-coming-soon">
      <div className="gallery-coming-soon-icon">
        <FaImage />
      </div>

      <h3 className="gallery-coming-soon-title">
        Deliverables coming soon
      </h3>

      <p className="gallery-coming-soon-subtitle">
        {isEditing && galleryEditingTab === 'sent'
          ? 'Your uploaded media will appear here once you submit files for editing.'
          : isEditing
          ? 'Your final edited media will appear here once the editor completes the processing.'
          : 'Photos will appear here once the photographer uploads the results of your shoot.'}
      </p>
    </div>
  );

  if (detailsLoading && !serviceName) {
    return <Loader />;
  }

  return (
    <div className="container gallery-container">


      {/* HOURLY SUB-NAVIGATION */}
      {isHourly && (
        <div className="hourly-sub-nav">
          {currentStep === "SERVICES" ? (
            <>
              <div className="plan-tabs editing-flow-tabs" style={{ marginBottom: '10px', borderBottom: 'none' }}>
                {hourlyPlanTabs.includes('standard') && (
                  <button 
                    className={`editing-tab ${galleryPlanTab === 'standard' ? 'active' : ''}`}
                    onClick={() => setGalleryPlanTab('standard')}
                  >
                    Standard
                  </button>
                )}
                {hourlyPlanTabs.includes('premium') && (
                  <button 
                    className={`editing-tab ${galleryPlanTab === 'premium' ? 'active' : ''}`}
                    onClick={() => setGalleryPlanTab('premium')}
                  >
                    Premium
                  </button>
                )}
              </div>

              {hourlyBookedHourTabs.length > 0 && (
                <div className="hour-tabs">
                  {hourlyBookedHourTabs.map((hour) => (
                    <button
                      key={hour}
                      type="button"
                      className={`hour-tab ${activeHourTab === hour ? "active" : ""}`}
                      onClick={() => setActiveHourTab(hour)}
                    >
                      {formatHourTabLabel(hour)}
                    </button>
                  ))}
                </div>
              )}

              <div className="service-cards">
                {[
                  { id: 'photography', icon: <FaCamera />, label: 'Photography' },
                  { id: 'videography', icon: <FaVideo />, label: 'Videography' },
                  { id: 'editor', icon: <FaEdit />, label: 'Editor' },
                  { id: 'lighting', icon: <FaLightbulb />, label: 'Lighting Setup' }
                ].filter(service => {
                  // Check if service is booked based on plan or cart items
                  const isPremium = galleryPlanTab === 'premium';
                  const bookedItems = bookingDetails?.items || [];
                  const isItemInCart = bookedItems.some(item => 
                    item.name.toLowerCase().includes(service.id) || 
                    item.name.toLowerCase().includes(service.label.toLowerCase())
                  );

                  // First, check hourlyPackages if they exist and we are in an hourly flow
                  const isHourlyFlow = serviceName?.toLowerCase().includes("hourly") || bookingDetails?.serviceCategory?.toLowerCase() === "hourly";
                  if (isHourlyFlow && bookingDetails?.hourlyPackages?.length > 0) {
                    const pkgs = bookingDetails.hourlyPackages;
                    const matchingPkgs = pkgs.filter(pkg => {
                      const pkgCategory = pkg.subCategoryType || pkg.planCategory || pkg.planId?.planCategory || pkg.planId?.teamCategory || pkg.category || "standard";
                      const matchesCategory = pkgCategory.toLowerCase().includes(galleryPlanTab?.toLowerCase());
                      let pkgHours = String(pkg.hours).replace(/[^0-9-]/g, '');
                      let activeH = activeHourTab.replace(/[^0-9-]/g, '');
                      const matchesHour = pkgHours === activeH || (activeH === "0-3" && (pkgHours === "0-3" || pkgHours === "3"));
                      return matchesCategory && matchesHour;
                    });

                    if (matchingPkgs.length === 0) return false; // If they didn't book this hour, hide the roles
                    
                    let isBookedInPkg = false;
                    matchingPkgs.forEach(pkg => {
                      if (service.id === 'photography') {
                        if (pkg.assignedPhotographers?.filter(v => v && String(v).trim() !== "" && String(v).trim() !== "null").length > 0 || pkg.services?.some(s => s.name?.toLowerCase().includes('photo'))) isBookedInPkg = true;
                      } else if (service.id === 'videography') {
                        if (pkg.assignedVideographers?.filter(v => v && String(v).trim() !== "" && String(v).trim() !== "null").length > 0 || pkg.services?.some(s => s.name?.toLowerCase().includes('video') || s.name?.toLowerCase().includes('cinema'))) isBookedInPkg = true;
                      } else if (service.id === 'editor') {
                        if (pkg.assignedEditors?.filter(v => v && String(v).trim() !== "" && String(v).trim() !== "null").length > 0 || pkg.services?.some(s => s.name?.toLowerCase().includes('edit'))) isBookedInPkg = true;
                      } else if (service.id === 'lighting') {
                        if ((pkg.assignedLightingSetups || pkg.assignedLighting)?.filter(v => v && String(v).trim() !== "" && String(v).trim() !== "null").length > 0 || pkg.services?.some(s => s.name?.toLowerCase().includes('light'))) isBookedInPkg = true;
                      }
                    });
                    return isBookedInPkg;
                  }

                  // Fallback for non-hourly or missing packages: Strictly follow cart items if available
                  let isBooked = false;
                  if (service.id === 'photography') {
                    isBooked = true; // Photography is the base service
                  } else if (bookedItems.length > 0) {
                    isBooked = isItemInCart; // Follow cart if items exist
                  } else if (isPremium) {
                    isBooked = true; // Fallback for premium if no items list
                  } else {
                    isBooked = false; // Standard fallback: Only photography is guaranteed (handled above)
                  }
                  return isBooked;
                }).map(service => {
                  return (
                    <div 
                      key={service.id}
                      className={`service-card ${activeServiceCard === service.id ? 'active' : ''}`}
                      onClick={() => {
                        setActiveServiceCard(service.id);
                        setCurrentStep("STAFF");
                      }}
                    >
                      <div className="service-card-icon">{service.icon}</div>
                      <div className="service-card-label">{service.label}</div>
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="fresh-page-view animate__animated animate__fadeIn">
              <div className="gallery-top-nav" style={{ display: "flex", justifyContent: "flex-start", marginBottom: "20px" }}>
                <button 
                  onClick={() => {
                    if (currentStep === "MEDIA") {
                      setCurrentStep("STAFF");
                      setActiveStaff(null);
                    } else {
                      setCurrentStep("SERVICES");
                    }
                  }} 
                className="admin-back-btn"
              >
                  &larr; Back
                </button>
              </div>
              
              {currentStep === "STAFF" && renderStaffFolders()}
            </div>
          )}
        </div>
      )}

      {/* EDITING SUB-NAVIGATION */}
      {isEditing && (
        <div className="editing-flow-tabs-container">
           {!isQuoteEditingBooking && (
             <div className="plan-tabs editing-flow-tabs" style={{ marginBottom: '10px', borderBottom: 'none' }}>
              <button 
                className={`editing-tab ${galleryPlanTab === 'standard' ? 'active' : ''}`}
                onClick={() => setGalleryPlanTab('standard')}
              >
                Standard
              </button>
              <button 
                className={`editing-tab ${galleryPlanTab === 'premium' ? 'active' : ''}`}
                onClick={() => setGalleryPlanTab('premium')}
              >
                Premium
              </button>
            </div>
           )}
          <div className="editing-flow-tabs">
            <button 
              className={`editing-tab ${galleryEditingTab === 'sent' ? 'active' : ''}`}
              onClick={() => setGalleryEditingTab('sent')}
            >
              Sent (My Uploads)
            </button>
            <button 
              className={`editing-tab ${galleryEditingTab === 'received' ? 'active' : ''}`}
              onClick={() => setGalleryEditingTab('received')}
            >
              Received (Final Results)
            </button>
          </div>
        </div>
      )}

      {(!isHourly || currentStep === "MEDIA") && (
        <>
          {/* HEADER */}
          <div className="gallery-header">
            <div className="gallery-left">
              {(!(isEditing && galleryEditingTab === 'sent') && visibleGalleryData.length > 0 && isPublished === true) && (
                <div className={`gallery-pending ${daysRemaining <= 5 ? "urgent" : ""}`}>
                  {!isFullPayment ? (
                    <span style={{ color: "#ff0000", fontWeight: "bold" }}>Please pay full amount to see media</span>
                  ) : isBlur ? (
                    <span style={{ color: "red", fontWeight: "bold" }}>Your gallery plan is expired</span>
                  ) : (
                    <>
                      <span style={{ color: "#ff0000", fontWeight: "bold" }}>{daysRemaining} days remaining..</span>
                    </>
                  )}
                </div>
              )}

              <div style={{ display: 'flex', gap: '10px' }}>
                {visibleGalleryData.length > 0 && !isBlur && (isPublished === true || (isEditing && galleryEditingTab === 'sent')) && (
                  <button
                    className="download-selected-btn"
                    onClick={() => {
                      if (visibleSelectedCount === visibleGalleryData.length) {
                        setSelectedItems((prev) =>
                          prev.filter((url) => !visibleGalleryData.some((item) => item.url === url))
                        );
                      } else {
                        setSelectedItems((prev) => [
                          ...prev.filter((url) => !visibleGalleryData.some((item) => item.url === url)),
                          ...visibleGalleryData.map((item) => item.url)
                        ]);
                      }
                    }}
                  >
                    {visibleSelectedCount === visibleGalleryData.length ? "Deselect All" : "Select All"}
                  </button>
                )}

                {visibleSelectedCount > 0 && (
                  <>
                    {isEditing && galleryEditingTab === 'sent' && (
                      <button
                        className="download-selected-btn"
                        onClick={deleteSelectedFiles}
                        style={{ backgroundColor: '#dc3545' }}
                      >
                        Delete Selected ({visibleSelectedCount})
                      </button>
                    )}
                    <button
                      className="download-selected-btn"
                      onClick={downloadSelected}
                    >
                      Download Selected ({visibleSelectedCount})
                    </button>
                  </>
                )}
              </div>
            </div>

            <div className="gallery-actions">
              {/* Case 1: Gallery Expired (Fully Paid but 14 days passed) */}
              {(isBlur === true && isFullPayment === true && isPublished === true) && (
                <button
                  className="gallery-btn unlock-btn"
                  onClick={() => setShowUnlockModal(true)}
                >
                  Unlock Images
                </button>
              )}

              {/* Case 2: Partial Payment Lock - Button Removed as requested */}



              {visibleGalleryData.length > 0 && isFullPayment === true && isPublished === true && !(isEditing && galleryEditingTab === 'sent') && (
                <button
                  className="gallery-btn"
                  onClick={handleDownloadZip}
                  disabled={isBlur === true}
                >
                  Download Zip
                </button>
              )}
            </div>
          </div>

          {((!isPublished || !visibleGalleryData.length) && !bookingId?.startsWith("dummy-") && !(isEditing && galleryEditingTab === 'sent')) ? (
            renderComingSoon()
          ) : (
            <>
              {isEditing && galleryEditingTab === 'sent' ? (
                <div 
                  ref={gridRef}
                  className="gallery-row-gap"
                  style={{
                    gridAutoRows: `${colWidth}px`,
                    minHeight: `${totalRows * rowHeight}px`
                  }}
                >
                  {visibleItems.length > 0 ? (
                    visibleItems.map((media, offset) => {
                      const idx = startIndex + offset;
                      const colIdx = idx % cols;
                      const rowIdx = Math.floor(idx / cols);
                      return (
                        <div 
                          key={media.id || idx}
                          style={{
                            gridColumn: `${colIdx + 1}`,
                            gridRow: `${rowIdx + 1}`
                          }}
                        >
                           <GalleryItem
                             src={media.imageUrl}
                             fileKey={media.key}
                             isBlur={false}
                             showDelete={true}
                             index={idx}
                             isSelected={selectedItems.includes(media.imageUrl)}
                             openLightbox={openLightbox}
                             deleteUserFile={deleteUserFile}
                             downloadFile={downloadFile}
                             toggleSelect={toggleSelect}
                           />
                        </div>
                      );
                    })
                  ) : (
                    renderComingSoon()
                  )}
                </div>
              ) : (
                <div 
                  ref={gridRef}
                  className="gallery-row-gap"
                  style={{
                    gridAutoRows: `${colWidth}px`,
                    minHeight: `${totalRows * rowHeight}px`
                  }}
                >
                  {visibleItems.length > 0 ? (
                    visibleItems.map((media, offset) => {
                      const idx = startIndex + offset;
                      const colIdx = idx % cols;
                      const rowIdx = Math.floor(idx / cols);
                      return (
                        <div 
                          key={media.id || idx}
                          style={{
                            gridColumn: `${colIdx + 1}`,
                            gridRow: `${rowIdx + 1}`
                          }}
                        >
                           <GalleryItem
                             src={media.imageUrl}
                             fileKey={media.key}
                             isBlur={isBlur}
                             index={idx}
                             isSelected={selectedItems.includes(media.imageUrl)}
                             openLightbox={openLightbox}
                             deleteUserFile={deleteUserFile}
                             downloadFile={downloadFile}
                             toggleSelect={toggleSelect}
                           />
                        </div>
                      );
                    })
                  ) : (
                    <p className="no-data text-center w-100 py-5">No files received yet.</p>
                  )}
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* INFINITE SCROLL */}

      {hasMore && (
        <div
          ref={loaderRef}
          style={{ textAlign: "center", padding: 20 }}
        >
          {loading && <Loader />}
        </div>
      )}

      <UnlockImageModal
        show={showUnlockModal}
        onClose={() => setShowUnlockModal(false)}
        bookingId={bookingId}              // pass correct bookingId
      // outstandingAmount={200}         // pass correct amount
      />

      {/* MESSAGE MODAL */}

      <CommonMessageModal
        show={showMessageModal}
        onClose={() => setShowMessageModal(false)}
        message={modalMessage}
        buttonText="Okay"
      />

      {/* CUSTOM PREMIUM LIGHTBOX */}
      <GalleryLightbox
        isOpen={lightboxOpen}
        items={visibleGalleryData}
        activeIndex={lightboxIndex}
        onClose={closeLightbox}
        onNext={handleLightboxNext}
        onPrev={handleLightboxPrev}
        isLoadingMore={loading && lightboxIndex === visibleGalleryData.length - 1}
        hasMore={hasMore}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      />
    </div>
  );
};

export default Gallery;
